/**
 * Creates date and time formatters based on the provided locale
 * @param locale The locale to use for formatting
 * @returns Object with formatDate and formatTime functions
 */
export function createDateFormatters(locale: string) {
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  return {
    /**
     * Format a date string or Date object to a localized date string
     * @param date The date to format
     * @returns Formatted date string or null if date is null
     */
    formatDate: (date: string | Date | null) => {
      if (!date) return null;
      return dateFormatter.format(typeof date === 'string' ? new Date(date) : date);
    },
    
    /**
     * Format a date string or Date object to a localized time string
     * @param date The date to format
     * @returns Formatted time string
     */
    formatTime: (date: string | Date) => {
      return timeFormatter.format(typeof date === 'string' ? new Date(date) : date);
    }
  };
}

/**
 * Format a date with timezone information
 * 
 * Shows the date in the user's local timezone for better usability
 * without exposing the specific timezone information.
 * 
 * @param date The date to format
 * @param timezone The original timezone of the date (unused, kept for backward compatibility)
 * @returns Formatted date string
 */
export function formatDateWithTimezone(date: string | Date, timezone?: string): string {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Always use the user's browser timezone for display
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  try {
    // Format the date using the user's local timezone
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimezone,
      hourCycle: 'h23' // Use 24-hour format for consistency
    }).format(dateObj);
  } catch (error) {
    // Fallback to UTC if there's an error with the timezone
    console.error(`Error formatting date with timezone ${userTimezone}:`, error);
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      hourCycle: 'h23'
    }).format(dateObj);
  }
}

/**
 * Check if a date is in the past (expired)
 * @param date The deadline date
 * @returns True if the date is in the past
 */
export function isDateExpired(date: string | Date): boolean {
  if (!date) return false;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  
  return now > dateObj;
}

/**
 * Get user's current timezone
 * @returns The user's timezone identifier
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Properly convert a datetime input value to ISO string, preserving the user's intended time
 * 
 * Important: datetime-local inputs don't include timezone information. The browser interprets the
 * input in the local timezone, but we need to ensure this time is stored consistently regardless
 * of where the user is located.
 * 
 * @param dateTimeInputValue The value from a datetime-local input
 * @returns ISO string of the selected date and time
 */
export function normalizeInputDatetime(dateTimeInputValue: string): string {
  if (!dateTimeInputValue) return '';
  
  // Create a date object from the input value
  // This will be interpreted in the user's local timezone
  const userSelectedDate = new Date(dateTimeInputValue);
  
  // Important: We're storing this exact time the user selected
  // but in ISO format for consistent storage
  return userSelectedDate.toISOString();
}

/**
 * Format a countdown timer from a number of seconds
 * @param seconds Total seconds remaining
 * @returns Formatted time string
 */
export function formatCountdown(seconds: number): string {
  if (seconds <= 0) return 'Expired';
  
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const days = Math.floor(seconds / (3600 * 24));
  
  // Create an array of time components in the format of [value, unit]
  const timeComponents: [number, string][] = [
    [days, 'd'],
    [hours, 'h'],
    [minutes, 'm'],
    [secs, 's']
  ];
  
  // Filter out zero values and join with spaces
  let result = timeComponents
    .filter(([value]) => value > 0)
    .map(([value, unit]) => `${value}${unit}`)
    .join(' ');
  
  // If all values are zero (edge case), show at least seconds
  if (!result) {
    result = '0s';
  }
  
  return result;
}

/**
 * Checks if a counter has expired based on its time limit settings
 * 
 * This function ensures consistent deadline checking regardless of the user's timezone
 * by comparing timestamps directly in milliseconds (which are timezone-independent).
 * 
 * @param counter The counter object to check
 * @returns Boolean indicating if the counter has expired
 */
export function isCounterExpired(counter: any): boolean {
  // If counter has a server-provided isExpired flag, use that
  if (counter?.isExpired !== undefined) {
    return counter.isExpired;
  }
  
  // If no time limit is set, counter is not expired
  if (!counter || !counter.timeLimitType) {
    return false;
  }
  
  const now = new Date();
  
  // Check deadline-based expiration
  if (counter.timeLimitType === 'deadline' && counter.deadline) {
    // Convert deadline string to Date object
    const deadlineDate = new Date(counter.deadline);
    
    // Compare timestamps in milliseconds (timezone-independent)
    return now.getTime() > deadlineDate.getTime();
  }
  
  // Check countdown-based expiration
  if (counter.timeLimitType === 'countdown' && 
      counter.countdownStartedAt && 
      counter.countdownMinutes) {
    const startedAt = new Date(counter.countdownStartedAt);
    const countdownMs = counter.countdownMinutes * 60 * 1000;
    const endTimeMs = startedAt.getTime() + countdownMs;
    
    // Compare timestamps in milliseconds
    return now.getTime() > endTimeMs;
  }
  
  return false;
} 